import axios, { type AxiosInstance } from "axios";

import { useAuth } from "./auth";
import type {
  LeaderboardEntry,
  Member,
  OtherTagging,
  PresignResponse,
  Project,
  Region,
  Tagging,
  TagDefinition,
  Task,
  User,
  UserListItem,
  UserUpdatePayload,
} from "./types";

const baseURL = "/api/v1";

export const api: AxiosInstance = axios.create({ baseURL });

api.interceptors.request.use((config) => {
  const token = useAuth.getState().token;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error?.response?.status === 401) {
      useAuth.getState().logout();
    }
    return Promise.reject(error);
  },
);

// ---------- API surface ----------

export const AuthApi = {
  async register(username: string, password: string, display_name?: string) {
    const { data } = await api.post<{ access_token: string }>("/auth/register", {
      username,
      password,
      display_name,
    });
    return data;
  },
  async login(username: string, password: string) {
    const { data } = await api.post<{ access_token: string }>("/auth/login", { username, password });
    return data;
  },
  async me() {
    const { data } = await api.get<User>("/auth/me");
    return data;
  },
};

export interface ProjectCreatePayload {
  name: string;
  description?: string | null;
  s3_bucket: string;
  s3_prefix: string;
  tag_schema?: TagDefinition[];
}

export const ProjectsApi = {
  async list() {
    return (await api.get<Project[]>("/projects")).data;
  },
  async get(id: number) {
    return (await api.get<Project>(`/projects/${id}`)).data;
  },
  async create(payload: ProjectCreatePayload) {
    return (await api.post<Project>("/projects", payload)).data;
  },
  async update(id: number, payload: Partial<ProjectCreatePayload>) {
    return (await api.patch<Project>(`/projects/${id}`, payload)).data;
  },
  async delete(id: number) {
    await api.delete(`/projects/${id}`);
  },
  async scan(id: number) {
    return (await api.post<{ project_id: number; tasks_created: number }>(`/projects/${id}/scan`)).data;
  },
  async leaderboard(id: number, limit = 3) {
    return (await api.get<LeaderboardEntry[]>(`/projects/${id}/leaderboard`, { params: { limit } })).data;
  },
};

export const TasksApi = {
  async list(projectId: number, opts?: { status?: string; my_state?: string }) {
    // Server caps each page at 1000. Loop until a short page tells us we've hit the end.
    const PAGE = 1000;
    const all: Task[] = [];
    for (let offset = 0; ; offset += PAGE) {
      const { data } = await api.get<Task[]>(`/tasks/by-project/${projectId}`, {
        params: { ...opts, limit: PAGE, offset },
      });
      all.push(...data);
      if (data.length < PAGE) break;
    }
    return all;
  },
  async next(projectId: number, after?: number) {
    const { data } = await api.get<Task>(`/tasks/by-project/${projectId}/next`, {
      params: after != null ? { after } : undefined,
    });
    return data;
  },
  async get(taskId: number) {
    return (await api.get<Task>(`/tasks/${taskId}`)).data;
  },
};

export const TaggingsApi = {
  async forTask(taskId: number) {
    return (await api.get<Tagging>(`/taggings/for-task/${taskId}`)).data;
  },
  async save(taskId: number, regions: Region[], submit: boolean) {
    const body = { regions, submit };
    return (await api.put<Tagging>(`/taggings/for-task/${taskId}`, body)).data;
  },
  async othersForTask(taskId: number) {
    return (await api.get<OtherTagging[]>(`/taggings/by-task/${taskId}/others`)).data;
  },
};

export const MembersApi = {
  async list(projectId: number) {
    return (await api.get<Member[]>(`/members/by-project/${projectId}`)).data;
  },
  async add(projectId: number, username: string) {
    return (await api.post<Member>(`/members/by-project/${projectId}`, { username })).data;
  },
  async remove(projectId: number, userId: number) {
    await api.delete(`/members/by-project/${projectId}/${userId}`);
  },
  async addAll(projectId: number) {
    return (await api.post<{ added: number }>(`/members/by-project/${projectId}/add-all`)).data;
  },
};

export const S3Api = {
  async presignAudio(taskId: number) {
    return (await api.get<PresignResponse>(`/s3/audio/${taskId}`)).data;
  },
};

export const UsersApi = {
  async list(): Promise<UserListItem[]> {
    return (await api.get<UserListItem[]>("/users")).data;
  },
  async update(id: number, payload: UserUpdatePayload): Promise<User> {
    return (await api.patch<User>(`/users/${id}`, payload)).data;
  },
};

export const ExportApi = {
  async cvat(projectId: number): Promise<unknown> {
    return (await api.get<unknown>(`/export/projects/${projectId}/cvat`)).data;
  },
};
